import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search as SearchIcon, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Search = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/reviews?search=${searchTerm}`);
      setReviews(data);
    } catch (error) {
      console.error('Error searching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={star <= rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  return (
    <div data-testid="search-page" className="min-h-screen bg-background py-16">
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="font-serif text-4xl font-bold mb-8">Search Reviews</h1>
        
        <form onSubmit={handleSearch} className="mb-12">
          <div className="relative">
            <input
              type="text"
              placeholder="Search for products..."
              data-testid="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-6 py-4 pr-24 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-lg"
            />
            <button
              type="submit"
              data-testid="search-submit-btn"
              className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2 bg-primary text-primary-foreground rounded-sm hover:opacity-90 smooth-transition flex items-center space-x-2"
            >
              <SearchIcon size={20} />
              <span>Search</span>
            </button>
          </div>
        </form>

        {loading && (
          <div data-testid="search-loading" className="text-center py-12">
            <p className="text-muted-foreground">Searching...</p>
          </div>
        )}

        {!loading && reviews.length > 0 && (
          <div className="space-y-6">
            <h2 className="font-serif text-2xl font-bold mb-6">
              Found {reviews.length} {reviews.length === 1 ? 'result' : 'results'}
            </h2>
            {reviews.map((review) => (
              <Link
                key={review.id}
                to={`/review/${review.slug}`}
                data-testid={`search-result-${review.slug}`}
                className="block border border-border p-6 hover-scale smooth-transition"
              >
                <div className="flex space-x-6">
                  <img
                    src={review.featured_image}
                    alt={review.title}
                    className="w-32 h-32 object-cover"
                  />
                  <div className="flex-1">
                    <span className="text-xs font-medium text-primary uppercase tracking-wide">
                      {review.category?.name}
                    </span>
                    <h3 className="font-serif text-2xl font-bold mt-2 mb-3">
                      {review.title}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {review.description.substring(0, 150)}...
                    </p>
                    <div className="flex items-center space-x-3">
                      {renderStars(review.rating)}
                      <span className="font-semibold">{review.rating}/5</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {!loading && searchTerm && reviews.length === 0 && (
          <div data-testid="no-results" className="text-center py-12">
            <p className="text-muted-foreground text-lg">No reviews found for "{searchTerm}"</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;